import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { shouldAutoPlay } from './auto-play'
import PARTICLE_SHAPES from './particle-shapes'
import { PARTICLES, SEASON_PARTICLES, getSeason } from './seasons'

const useStyles = makeStyles({
  '@keyframes seasonalLogoDrop': {
    '0%': { opacity: 0, transform: 'translateY(-35%)' },
    '12%': { opacity: 1 },
    '85%': { opacity: 1 },
    '100%': { opacity: 0, transform: 'translateY(90%)' },
  },
  '@keyframes seasonalLogoSway': {
    from: {
      transform:
        'translateX(calc(var(--particle-dir) * var(--particle-sway, 1) * -30%)) rotate(calc(var(--particle-dir) * var(--particle-sway, 1) * -35deg))',
    },
    to: {
      transform:
        'translateX(calc(var(--particle-dir) * var(--particle-sway, 1) * 30%)) rotate(calc(var(--particle-dir) * var(--particle-sway, 1) * 35deg))',
    },
  },
  root: {
    position: 'relative',
    display: 'flex',
    flexShrink: 0,
  },
  // No box of its own, so the wrapper never affects layout while idle.
  inactive: {
    display: 'contents',
  },
  // Sized so a fall runs from just above the logo to just below it, which
  // keeps particles inside the header bar instead of off the top of the page.
  particles: {
    position: 'absolute',
    top: '12%',
    bottom: '-32%',
    left: 0,
    right: 0,
    pointerEvents: 'none',
  },
  drop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0,
    animationName: '$seasonalLogoDrop',
    animationDuration: 'var(--particle-duration)',
    animationDelay: 'var(--particle-delay)',
    animationTimingFunction: 'ease-in-out',
    animationFillMode: 'forwards',
  },
  sway: {
    position: 'absolute',
    top: 0,
    left: 'var(--particle-x)',
    display: 'block',
    width: 'var(--particle-size)',
    height: 'auto',
    animationName: '$seasonalLogoSway',
    animationDuration: 'calc(var(--particle-duration) / 3)',
    animationDelay: 'var(--particle-delay)',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 3,
    animationDirection: 'alternate',
  },
})

/**
 * Always renders its wrapper (with `display: contents` while idle) so the
 * children are never re-created when the flag resolves after first paint.
 * `enabled` is whether the animation may play at all.
 */
const SeasonalLogoFrame = ({ enabled, now, children }) => {
  const classes = useStyles()
  const { palette } = useTheme()
  const [run, setRun] = useState(0)
  const [playing, setPlaying] = useState(false)
  const hovering = useRef(false)
  const finished = useRef(0)

  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    {
      noSsr: true,
    },
  )
  const config = SEASON_PARTICLES[getSeason(now)]
  const active = enabled && !prefersReducedMotion && Boolean(config)

  useEffect(() => {
    if (active && shouldAutoPlay()) {
      setPlaying(true)
    }
  }, [active])

  const handlePointerEnter = () => {
    hovering.current = true
    if (!playing) {
      finished.current = 0
      setRun(current => current + 1)
      setPlaying(true)
    }
  }

  const handlePointerLeave = () => {
    hovering.current = false
  }

  const handleDropEnd = event => {
    // Ignore the sway animation's end event bubbling up from the SVG.
    if (event.target !== event.currentTarget) return

    finished.current += 1
    if (finished.current < PARTICLES.length) return

    finished.current = 0
    if (hovering.current) {
      setRun(current => current + 1)
    } else {
      setPlaying(false)
    }
  }

  const Shape = config && PARTICLE_SHAPES[config.shape]
  const { fills, stroke } = config ? config.getColors(palette) : {}

  return (
    <span
      className={clsx(classes.root, !active && classes.inactive)}
      data-testid="seasonal-logo"
      onPointerEnter={active ? handlePointerEnter : undefined}
      onPointerLeave={active ? handlePointerLeave : undefined}
    >
      {children}
      {active && playing && (
        <span
          key={run}
          className={classes.particles}
          style={{ '--particle-sway': config.sway }}
          aria-hidden="true"
          data-testid="seasonal-logo-particles"
          data-season={getSeason(now)}
        >
          {PARTICLES.map(({ x, size, delay, duration, direction }, index) => (
            <span
              key={x}
              className={classes.drop}
              data-testid="seasonal-logo-particle"
              onAnimationEnd={handleDropEnd}
              style={{
                '--particle-x': `${x * 100}%`,
                '--particle-size': `${size * 100}%`,
                '--particle-delay': `${delay}s`,
                '--particle-duration': `${duration}s`,
                '--particle-dir': direction,
              }}
            >
              <Shape
                className={classes.sway}
                fill={fills[index % fills.length]}
                stroke={stroke}
              />
            </span>
          ))}
        </span>
      )}
    </span>
  )
}

SeasonalLogoFrame.propTypes = {
  enabled: PropTypes.bool.isRequired,
  /** Date used to pick the season; defaults to the current local date. */
  now: PropTypes.instanceOf(Date),
  children: PropTypes.node.isRequired,
}

export default SeasonalLogoFrame
